import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FileInput from '../reusable/FileInput';
import { handleFormValidationErrors } from '../../utils/utils';

const CreatePostModal = ({ onClose, onSubmit, categories, initialData = null }) => {
    const [formData, setFormData] = useState({
        blog_category_id: initialData?.blog_category_id || '',
        title: initialData?.title || '',
        content: initialData?.content || '',
        status: 'published',
        image: '' // Keep empty unless updating image specifically
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const isEdit = !!initialData;

    const validate = () => {
        const newErrors = {};
        if (!formData.blog_category_id && !isEdit) newErrors.blog_category_id = "Category is required";
        if (!formData.title) newErrors.title = "Title is required";
        else if (formData.title.length > 50) newErrors.title = "Title cannot exceed 50 characters";
        if (!formData.content) newErrors.content = "Content is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});

        if (!validate()) return;

        setLoading(true);
        try {
            await onSubmit(formData, initialData?.id);
        } catch (error) {
            console.error(error);
            handleFormValidationErrors(error, setErrors);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-6 z-[100] overflow-y-auto"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
                <motion.div
                    className="bg-white rounded-[32px] w-full max-w-3xl overflow-hidden shadow-2xl relative"
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 20 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="p-8 md:p-12">
                        <div className="flex justify-between items-center mb-10">
                            <div>
                                <h2 className="text-3xl font-black text-gray-900">{isEdit ? 'Edit Story' : 'Create New Story'}</h2>
                                <p className="text-gray-500 mt-1 font-medium">{isEdit ? 'Refine your thoughts' : 'Share your insights with the world'}</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="w-10 h-10 bg-gray-50 hover:bg-gray-100 rounded-full flex items-center justify-center transition-colors text-xl font-light"
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-bold uppercase tracking-wider text-gray-400 mb-2 px-1">Category</label>
                                    <select
                                        required
                                        className={`w-full h-14 px-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-600 font-semibold text-gray-700 transition-all appearance-none cursor-pointer ${errors.blog_category_id ? 'ring-2 ring-red-500' : ''}`}
                                        value={formData.blog_category_id}
                                        onChange={(e) => setFormData({ ...formData, blog_category_id: e.target.value })}
                                        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.5em' }}
                                    >
                                        <option value="">Select a category</option>
                                        {categories.map(category => (
                                            <option key={category.id} value={category.id}>{category.name}</option>
                                        ))}
                                    </select>
                                    {errors.blog_category_id && <p className="text-red-500 text-xs mt-1 font-bold px-1">{errors.blog_category_id}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-bold uppercase tracking-wider text-gray-400 mb-2 px-1">Title</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="Give your story a catchy title"
                                        className={`w-full h-14 px-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-600 font-semibold text-gray-700 transition-all ${errors.title ? 'ring-2 ring-red-500' : ''}`}
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    />
                                    {errors.title && <p className="text-red-500 text-xs mt-1 font-bold px-1">{errors.title}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-bold uppercase tracking-wider text-gray-400 mb-2 px-1">Cover Image</label>
                                    <FileInput
                                        id="blog-image"
                                        onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                                        className="!h-48"
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col">
                                <label className="block text-sm font-bold uppercase tracking-wider text-gray-400 mb-2 px-1">Content</label>
                                <textarea
                                    required
                                    placeholder="Write your story here..."
                                    className={`w-full flex-1 p-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-600 font-medium text-gray-700 transition-all resize-none leading-relaxed ${errors.content ? 'ring-2 ring-red-500' : ''}`}
                                    value={formData.content}
                                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                />
                                {errors.content && <p className="text-red-500 text-xs mt-1 font-bold px-1">{errors.content}</p>}
                            </div>

                            <div className="md:col-span-2 flex justify-end gap-4 mt-4">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-8 py-4 rounded-2xl text-gray-500 font-bold hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-2xl font-bold transition-all shadow-xl shadow-blue-100 disabled:opacity-50 active:scale-95 flex items-center gap-2"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            {isEdit ? 'Updating...' : 'Publishing...'}
                                        </>
                                    ) : (isEdit ? 'Update Story' : 'Publish Story')}
                                </button>
                            </div>
                        </form>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default CreatePostModal;